<template>
  <div data-view="daily-reports-cpm">
    <layout-logged-frame :title="$t('daily_stats')">
      <template v-slot:header-actions>
        <header-actions :actions="headerActions" />
      </template>

      <template v-slot:content>
        <div>
          <CPMReportsAlert />

          <ReportFilters
            :title="$t('cpm_reports')"
            :is-skeleton-loading="isSkeletonLoading"
            @filter="onFilter"
            @clearFilters="onClearFilters"
          >
            <template v-slot:phone-actions>
              <sort-by-select
                :columns="columns"
                :pagination="phoneSort"
                :on-update-pagination="onPhoneSortChange"
                :sync-to-url="false"
              />
            </template>
          </ReportFilters>

          <table-base-client-sort
            has-phone-layout
            :phone-sort="phoneSort"
            :columns="columns"
            :items="dailyReports"
            :is-loading="isLoading"
            :no-data-title="$t('no_reports')"
            :no-data-subtitle="$t('start_to_use_our_products')"
            has-custom-items-template
            >
            <template v-slot:item-cell="scope">
              <template v-if="notAvailableItem(scope.cell)">
                <div :class="getColumnClass(scope.cell.column)">
                  <span class="not-available">
                    {{ $t('not_available') }}
                  </span>
                  <Tooltip
                    :text="$t('cpm_reports_table_not_available_tooltip')"
                    :link="helpReportsUrl"
                    position="left"
                    :delay="1000"
                  />
                </div>
              </template>

              <template v-else-if="scope.cell.column.value === 'ad'">
                <div>
                  {{ displayAdName(scope.cell.item, scope.cell.column.value) }}
                </div>
              </template>

              <template v-else-if="scope.cell.column.value === 'report_date'">
                <div :class="getColumnClass(scope.cell.column)">
                  {{
                    displayColumnValue(scope.cell.item, scope.cell.column.value)
                  }}
                </div>
              </template>

              <template v-else-if="scope.cell.column.value === 'start_date'">
                <div :class="getColumnClass(scope.cell.column)">
                  {{
                    displayDateColumn(scope.cell.item, scope.cell.column.value)
                  }}
                </div>
              </template>

              <template v-else-if="scope.cell.column.value === 'end_date'">
                <div :class="getColumnClass(scope.cell.column)">
                  {{
                    displayDateColumn(scope.cell.item, scope.cell.column.value)
                  }}
                </div>
              </template>

              <template v-else-if="scope.cell.column.value === 'country'">
                <div :class="getColumnClass(scope.cell.column)">
                  {{
                    displayColumnValue(scope.cell.item, scope.cell.column.value)
                      .name
                  }}
                </div>
              </template>

              <template
                v-else-if="
                  scope.cell.column.value === 'impressions' ||
                    scope.cell.column.value === 'total_clickouts' ||
                    scope.cell.column.value === 'organic_clickouts' ||
                    scope.cell.column.value === 'paid_clickouts' ||
                    scope.cell.column.value === 'total_deal_visits' ||
                    scope.cell.column.value === 'organic_deal_visits' ||
                    scope.cell.column.value === 'paid_deal_visits'
                "
              >
                <div :class="getColumnClass(scope.cell.column)">
                  {{ displayColumnAsIntlNumber(scope.cell) }}
                </div>
              </template>

              <template
                v-else-if="
                  scope.cell.column.value === 'deal_visit_boost' ||
                    scope.cell.column.value === 'clickout_boost' ||
                    scope.cell.column.value === 'click_through_rate'
                "
              >
                <div :class="getColumnClass(scope.cell.column)">
                  {{ displayColumnAsIntlNumber(scope.cell) }} %
                </div>
              </template>

              <template v-else-if="scope.cell.column.value === 'rate'">
                <div :class="getColumnClass(scope.cell.column)">
                  {{
                    displayColumnAsIntlNumber(
                      scope.cell,
                      (options = { minimumFractionDigits: 2 }),
                    )
                  }}
                  {{ displayCurrencySymbol(scope.cell) }}
                </div>
              </template>

              <template v-else-if="scope.cell.column.value === 'commission'">
                <div :class="getColumnClass(scope.cell.column)">
                  {{
                    displayColumnAsIntlNumber(
                      scope.cell,
                      (options = { minimumFractionDigits: 2 }),
                    )
                  }}
                  {{ displayCurrencySymbol(scope.cell) }}
                </div>
              </template>

              <template v-else>
                <div :class="getColumnClass(scope.cell.column)">
                  {{
                    displayColumnValue(scope.cell.item, scope.cell.column.value)
                  }}
                </div>
              </template>
            </template>
          </table-base-client-sort>
        </div>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import CPMReportsAlert from '../../components/alerts/CPMReports';
  import ReportFilters from '../../components/forms/filters/ReportsCPM';
  import Tooltip from '../../components/tooltips/Tooltip';
  import columns from '../../config/reports/columnsCPM';
  import {
    isArr,
    isObj,
    extractNestedProp,
    openUrlInNewWindow,
    getNavigatorLanguage,
  } from '../../utils';

  export default {
    components: {
      CPMReportsAlert,
      ReportFilters,
      Tooltip,
    },
    data() {
      return {
        phoneSort: {},
        query: {},
        isSkeletonLoading: true,
        pagination: {
          order: 'desc' ,
          page: 1,
          rowsPerPage: 15,
          sortBy: '',
          totalItems: 0,
          search: '',
        },
        columns,
        helpReportsUrl: import.meta.env.VUE_APP_HELP_REPORTS_URL,
        isLoading: false,
        isDownloadingReport: false,
      };
    },
    computed: {
      headerActions() {
        return [
          {
            key: 'download',
            icon: 'arrow_downward',
            title: `${this.$t('download')} .csv`,
            isDisabled: !this.hasDailyReports,
            isLoading: this.isDownloadingReport,
            onClick: this.downloadReport,
          },
        ];
      },
      activeUser() {
        return this.$store.getters['auth/user'];
      },
      dailyReports() {
        const { items } = this.$store.getters['reports/dailyCpm'];
        return items;
      },
      hasDailyReports() {
        const { dailyReports } = this;
        return isArr(dailyReports) && dailyReports.length > 0;
      },
    },
    methods: {
      onPhoneSortChange({ sortBy, order }) {
        this.phoneSort = { sortBy, order };
      },
      wrapQuery(options = {}) {
        const {
          page = 1,
          businessId = -1,
          merchantId = -1,
          date_range = '',
        } = options;

        return {
          page,
          businessId,
          merchantId,
          date_range
        };
      },
      getReports() {
        this.isLoading = true;

        const query = this.wrapQuery({ ...this.query});

        return this.$store
          .dispatch('reports/getDailyCpm', query)
          .then((res) => {

            this.query = query;
            this.pagination.totalItems = res.items.length;
            this.isLoading = false;

            this.isSkeletonLoading = false;

            return res;
          })
          .catch((err) => {
            this.isSkeletonLoading = false;

            this.isLoading = false;
            return err;
          });
      },
      getSearchQuery({ business, merchant, dates }) {
        const query = {};

        query.businessId = isObj(business) ? business.id : -1;
        query.merchantId = isObj(merchant) ? merchant.id : -1;
        query.date_range = dates;

        return query;
      },
      onFilter(filters) {
        this.query = {
          ...this.query,
          page: 1,
          ...this.getSearchQuery(filters)
        };

        this.getReports();
      },
      onClearFilters(filters) {
        this.query = {
          ...this.query,
          page: 1,
          ...this.getSearchQuery(filters)
        };

        this.getReports();
      },
      downloadReport() {
        const { businessId, merchantId, date_range } = this.query;
        const query = {
          businessId,
          merchantId,
          date_range,
        };

        this.isDownloadingReport = true;

        return this.$store
          .dispatch('reports/downloadDailyCpm', query)
          .then((res) => {
            this.isDownloadingReport = false;
            return res;
          })
          .catch((err) => {
            this.isDownloadingReport = false;
            return err;
          });
      },
      openUrl(event, url) {
        event.preventDefault();
        event.stopPropagation();
        return openUrlInNewWindow(window.open(), url);
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
      displayDateColumn(item, columnValue) {
        return extractNestedProp(item, columnValue).split(' ')[0];
      },
      displayColumnAsIntlNumber(cell, options) {
        const { item, column } = cell;
        const extactedNumber = extractNestedProp(item, column.value);
        const countryCode = getNavigatorLanguage() || 'en-US';

        return options
          ? // eslint-disable-next-line compat/compat
            new Intl.NumberFormat(countryCode, options).format(extactedNumber)
          : // eslint-disable-next-line compat/compat
            new Intl.NumberFormat(countryCode).format(extactedNumber);
      },
      displayCurrencySymbol(cell) {
        const { item } = cell;
        return isObj(item.country) && isObj(item.country.currency)
          ? item.country.currency.symbol
          : '€';
      },
      displayAdName(item, columnValue) {
        const name = extractNestedProp(item, columnValue);

        if (!name) {
          return '';
        }

        if (name.length < 40) {
          return name;
        }

        return `${name.substring(0, 40)}...`;
      },
      notAvailableItem(cell) {
        const { item, column } = cell;
        const isItemValid = extractNestedProp(item, column.value);

        return !isItemValid;
      },
      getColumnClass(column) {
        return column.align === 'end' ? 'd-flex justify-end' : 'd-flex';
      },
    },
  };
</script>

<style lang="scss">
@use '../../assets/styles/theme' as *;

[data-view='daily-reports-cpm'] {
  table {
    th, td {
      &:first-of-type {
        white-space: nowrap;
        min-width: 78px;
      }
    }

    th {
      &:first-of-type {
        padding-left: 15px;
      }
      &:last-of-type {
        padding-right: 15px;
      }
    }

    tbody {
      tr {
        td {
          .highlighted {
            color: #212121 !important;
            font-weight: 700;

            .v-theme--dark & {
              color: rgb(var(--v-theme-on-surface)) !important;
            }
          }
          .not-available {
            padding-right: 4px;
          }
          &:first-of-type {
            padding-left: 15px;
          }
          &:last-of-type {
            padding-right: 15px;
          }
        }
      }
    }
  }
}
</style>
